// SheetLab — experimental "kit of parts" sheet builder.
//
// Premise: assemble a day sheet by clicking widgets in a left palette;
// the right rail shows the timeline auto-sorted chronologically. Each
// click on the left adds an item; flights split into two timeline rows
// (departure + arrival) on add. Travel records (flights / hotels / car
// services) created here persist as real records and reappear in the
// palette as reusable buttons for any future sheet.
//
// Lives at /admin/sheets/lab. Sibling of the existing SheetBuilder.
// Reuses the shared API helpers in lib/api so server contracts stay in
// sync — nothing about the data model is new.

import React, { useEffect, useMemo, useState } from 'react';
import { api, apiFetch } from '../lib/api';
import { localizeEventTime, shortCodeForIana } from '../lib/tz';
import { groupEventsByZone, formatInstantInZone, formatInstantDateInZone } from '../lib/zoneBands';
import { usePeople } from '../context/PeopleContext';
import { TravelFormModal } from './Travel';
import { EventModal, CalendarPicker, LocationSearch, resolveInitials } from './SheetBuilder';
import SendModal from '../components/ReviewAndSendModal';
import useIsMobile from '../hooks/useIsMobile';

const SECTION = {
  marginBottom: 18,
};
const SECTION_LABEL = {
  fontSize: 11,
  fontWeight: 800,
  color: 'var(--text3)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: 8,
};
const PALETTE_BTN = {
  width: '100%',
  textAlign: 'left',
  background: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '10px 12px',
  marginBottom: 6,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  color: 'var(--text)',
  fontSize: 13,
};
// Brand-aligned palette tile — mirrors the tCard chip style used in
// SheetBuilder's preview rail (1.5px ${color}30 border, ${color}06
// fill, FA icon at 18px, 9px label) so palette tiles look like the
// chips they produce on the rendered sheet.
function tileStyle(color) {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: 12,
    minHeight: 70,
    background: `${color}06`,
    border: `1.5px solid ${color}30`,
    borderRadius: 12,
    color,
    cursor: 'pointer',
    transition: 'all 0.15s',
  };
}
const TIMELINE_ROW = {
  background: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: '12px 14px',
  marginBottom: 8,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

// Pull soundcheck / doors / showtime / opener times out of a venue's
// notes block. Notes are saved as plain text (`Soundcheck: 16:00`,
// `Doors: 7:00 PM`, `Opener: Olivia · 8:00 PM`) so we accept both
// 24-hour and 12-hour formats and normalize to a 24-hour HH:MM. Each
// `Opener:` line surfaces as its own slot keyed by the opener's name
// so a multi-act bill can have separate Olivia / Lauv / etc. palette
// items. Returns slots sorted by time so the palette reads soundcheck
// → doors → opener(s) → showtime regardless of source ordering.
function parseVenueSlots(notes) {
  if (!notes) return [];
  const slots = [];
  // Helper that normalizes "4:00 PM" or "16:00" to "HH:MM" 24-hour.
  const normalizeTime = (raw) => {
    const s = (raw || '').trim();
    const m12 = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (m12) {
      let h = Number(m12[1]) % 12;
      if (m12[3].toUpperCase() === 'PM') h += 12;
      return `${String(h).padStart(2, '0')}:${String(Number(m12[2])).padStart(2, '0')}`;
    }
    const m24 = s.match(/^(\d{1,2}):(\d{2})$/);
    if (m24) return `${String(Number(m24[1])).padStart(2, '0')}:${String(Number(m24[2])).padStart(2, '0')}`;
    return null;
  };
  // Single-instance slots (Soundcheck / Doors / Showtime). Anchored at
  // line start so a stray "Doors:" inside another note doesn't double-fire.
  for (const label of ['Soundcheck', 'Doors', 'Showtime']) {
    const re = new RegExp(`^\\s*${label}\\s*:\\s*(.+?)\\s*$`, 'mi');
    const m = notes.match(re);
    if (!m) continue;
    const time = normalizeTime(m[1]);
    if (time) slots.push({ label, time });
  }
  // Multi-instance Opener lines: "Opener: Olivia · 8:00 PM" — name
  // is required, time is required (otherwise we can't sort it).
  // Allow either "·" or "-" or "—" as the separator.
  const openerRe = /^\s*Opener\s*:\s*(.+?)\s*[·\-—]\s*([0-9]{1,2}:[0-9]{2}(?:\s*(?:AM|PM))?)\s*$/gim;
  let om;
  while ((om = openerRe.exec(notes)) !== null) {
    const name = om[1].trim();
    const time = normalizeTime(om[2]);
    if (!name || !time) continue;
    slots.push({ label: `Opener: ${name}`, time });
  }
  // Sort by time so palette is chronological.
  slots.sort((a, b) => a.time.localeCompare(b.time));
  return slots;
}

// Resolve the color stripe for an event's "who" — same fuzzy lookup
// rules SheetBuilder uses, so colors stay consistent across both
// builders. Returns the neutral fallback when no person is set or no
// match is found in the colorMap.
function findPersonColor(colorMap, name) {
  if (!name) return '#e6edf3';
  if (colorMap[name]) return colorMap[name];
  const lower = name.toLowerCase();
  const key = Object.keys(colorMap).find(k => k.toLowerCase() === lower);
  if (key) return colorMap[key];
  const firstName = lower.split(' ')[0];
  const partial = Object.keys(colorMap).find(k => k.toLowerCase().split(' ')[0] === firstName);
  if (partial) return colorMap[partial];
  return '#e6edf3';
}

// Compress an IANA tz id to a short tag for chip display.
//   "America/New_York"   → "ET"
//   "America/Los_Angeles"→ "PT"
//   "Europe/London"      → "GMT"
// Falls back to the city portion ("Buenos_Aires" → "Buenos Aires")
// when we don't have a known mapping, so any zone still reads.
function shortTimezone(iana) {
  if (!iana) return '';
  const map = {
    'America/New_York': 'ET',
    'America/Detroit': 'ET',
    'America/Toronto': 'ET',
    'America/Chicago': 'CT',
    'America/Denver': 'MT',
    'America/Phoenix': 'MST',
    'America/Los_Angeles': 'PT',
    'America/Vancouver': 'PT',
    'America/Anchorage': 'AKT',
    'Pacific/Honolulu': 'HST',
    'Europe/London': 'GMT',
    'Europe/Paris': 'CET',
    'Europe/Berlin': 'CET',
    'Europe/Madrid': 'CET',
    'Asia/Tokyo': 'JST',
    'Asia/Hong_Kong': 'HKT',
    'Asia/Singapore': 'SGT',
    'Asia/Dubai': 'GST',
    'Australia/Sydney': 'AET',
    'Australia/Melbourne': 'AET',
  };
  if (map[iana]) return map[iana];
  // Fallback: last segment, underscores → spaces.
  const tail = iana.split('/').pop() || iana;
  return tail.replace(/_/g, ' ');
}

// True when the event explicitly carries a tz that differs from the
// sheet's working tz — that's the "show a badge" case.
function isOutOfZone(event, sheetTz) {
  if (!event?.timezone) return false;
  if (!sheetTz) return false;
  return event.timezone !== sheetTz;
}

// Merge a freshly-refetched event list with whatever's in local
// state. Server is the source of truth for fields, but we KEEP any
// locally-known event that the server didn't return (orphans whose
// daysheetId never made it into sheet.eventIds). Without this every
// add-travel / delete-chip refetch would silently nuke pre-existing
// timeline rows.
function mergeEvents(local, fromServer) {
  const serverById = new Map((fromServer || []).map(e => [e.id, e]));
  const out = [];
  const seen = new Set();
  for (const e of fromServer || []) {
    out.push(e);
    seen.add(e.id);
  }
  for (const e of local || []) {
    if (!seen.has(e.id)) {
      out.push(e);
      seen.add(e.id);
    }
  }
  return out;
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

// Stable signature for de-duping clicks on the same palette item.
function paletteSig(kind, refId) {
  return `${kind}:${refId}`;
}

export default function SheetLab() {
  const isMobile = useIsMobile();
  const { colorMap } = usePeople();
  const [artists, setArtists] = useState([]);
  // Primary artist (used for fetching gcal/travel/sheet data) plus
  // the full set of selected artists for joint sheets. Mirrors the
  // selArtist / selArtistIds split in SheetBuilder.
  const [artistId, setArtistId] = useState('');
  const [selArtistIds, setSelArtistIds] = useState([]);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  // Starting location — drives the weather block on the published
  // sheet AND the sheet's working timezone (server geocodes both on
  // PATCH). Per-event timezone overrides ride on each event row and
  // are surfaced as a small zone badge next to the time.
  const [city, setCity] = useState('');
  const [weather, setWeather] = useState(null);
  const [sheetTimezone, setSheetTimezone] = useState(''); // IANA, e.g. "America/New_York"

  // Sheet on disk (created lazily on first add). null until first add fires.
  const [sheetId, setSheetId] = useState(null);
  const [sheetStatus, setSheetStatus] = useState('draft'); // 'draft' | 'published'
  const [publishing, setPublishing] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(''); // last-extract status line
  const [sheetEvents, setSheetEvents] = useState([]); // [{id, title, timeStart, timeEnd, location, travelType, travelId, nextDay, notes}]
  const [creatingSheet, setCreatingSheet] = useState(false);

  // Palette data
  const [gcalEvents, setGcalEvents] = useState([]);
  const [travel, setTravel] = useState([]); // all travel records for the artist
  // Every venue event the artist has — pulled across all sheets so a
  // venue created on one day can be re-added from another (matches
  // how hotel records persist across sheets via the travel store).
  const [allVenues, setAllVenues] = useState([]);
  // Every event the artist has, ever — kept around so cloning a venue
  // from another date can ALSO clone the soundcheck / doors / showtime
  // rows that were spawned alongside it.
  const [allArtistEvents, setAllArtistEvents] = useState([]);

  const [busyKey, setBusyKey] = useState(null); // palette button currently saving

  // Travel composer state. Reuses the rich TravelFormModal from /admin/travel
  // so flight lookup, private aviation, passengers, greeters, and drivers
  // are available without re-implementing them here. We pass a stub record
  // with just `type` set so the modal opens on the right tab in CREATE mode
  // (it gates edit-mode on record.id, not on record itself).
  const [travelModalType, setTravelModalType] = useState(null); // 'flight' | 'hotel' | 'carservice' | null
  const [editingTravel, setEditingTravel] = useState(null); // existing travel record being edited
  const [newEventOpen, setNewEventOpen] = useState(false);
  const [eventSeed, setEventSeed] = useState(null);
  const [venueModalOpen, setVenueModalOpen] = useState(false);
  // Click a timeline row to open the rich EventModal in edit mode.
  // Holds the event being edited (or null when no editor is active).
  const [editingEvent, setEditingEvent] = useState(null);
  // sheet.notes lives at the top of the timeline as a yellow card,
  // mirroring the published-sheet look. The card is the drop target —
  // drop a timeline event onto it and the row's title/time/location
  // auto-appends to the note body.
  const [sheetNotes, setSheetNotes] = useState('');
  const [showNoteCard, setShowNoteCard] = useState(false);

  // Initial load: artists
  useEffect(() => {
    api.getArtists().then(d => {
      const list = d.artists || [];
      setArtists(list);
      if (list[0]?.id && !artistId) {
        setArtistId(list[0].id);
        setSelArtistIds([list[0].id]);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line

  // When artist+date change, load palette feed
  useEffect(() => {
    if (!artistId || !date) return;
    // Date-change race guard: when the user picks a new date the old
    // GCal fetch may still be in flight. If it resolves after the new
    // one, its events bleed into the new date's palette (the user
    // saw JB Tour BA from May 6 leak into the May 20 lab). Capture
    // the date+artist this effect run owns; bail out of any setState
    // call whose owning render has been superseded.
    let stale = false;
    // Clear immediately so the previous date's events never paint
    // alongside the new date's loading state.
    setGcalEvents([]);
    api.getCalEvents(artistId, date)
      .then(d => { if (!stale) setGcalEvents(d.events || []); })
      .catch(() => { if (!stale) setGcalEvents([]); });
    api.getAllTravel(artistId)
      .then(list => { if (!stale) setTravel(Array.isArray(list) ? list : (list?.travel || [])); })
      .catch(() => { if (!stale) setTravel([]); });
    api.getEventsByArtist(artistId)
      .then(d => {
        if (stale) return;
        const events = d.events || [];
        setAllArtistEvents(events);
        setAllVenues(events.filter(e => e.travelType === 'venue'));
      })
      .catch(() => { if (!stale) { setAllArtistEvents([]); setAllVenues([]); } });
    return () => { stale = true; };
  }, [artistId, date]);

  // When artist+date change, look for an existing sheet and adopt it
  // so re-opening the page isn't destructive.
  useEffect(() => {
    if (!artistId || !date) return;
    setSheetId(null);
    setSheetEvents([]);
    setSheetNotes('');
    setShowNoteCard(false);
    setCity('');
    setWeather(null);
    setSheetTimezone('');
    api.getSheets(artistId).then(d => {
      const match = (d.sheets || []).find(s => s.date === date);
      if (match) {
        setSheetId(match.id);
        setSheetNotes(match.notes || '');
        setShowNoteCard(!!match.notes);
        setCity(match.city || '');
        setWeather(match.weather || null);
        setSheetTimezone(match.timezone || '');
        setSheetStatus(match.status || 'draft');
        // Pull in the existing events linked to that sheet.
        api.getEvents(match.id).then(ev => {
          setSheetEvents(prev => mergeEvents(prev, ev.events || []));
        }).catch(() => {});
      }
    }).catch(() => {});
  }, [artistId, date]);

  // Debounced PATCH for sheet.notes — saves after the user pauses
  // typing or 600ms after a drag-drop autofill.
  useEffect(() => {
    if (!sheetId) return;
    const t = setTimeout(() => {
      api.updateSheet(sheetId, { notes: sheetNotes }).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [sheetNotes, sheetId]);

  // PATCH city when it changes — the server re-geocodes, refreshes
  // weather, and auto-tags sheet.timezone from the same forecast
  // call. We refresh both locally so the chip + tz badge re-render.
  useEffect(() => {
    if (!sheetId) return;
    const t = setTimeout(() => {
      api.updateSheet(sheetId, { city: city || null })
        .then(updated => {
          if (updated?.weather !== undefined) setWeather(updated.weather);
          if (updated?.timezone) setSheetTimezone(updated.timezone);
        })
        .catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [city, sheetId]);

  // Sort timeline chronologically. nextDay rows fall to the bottom under
  // a divider so the user can see the day boundary visually.
  // Hotel/carservice events are hidden — they live in the hero chip
  // strip at the top instead, mirroring the published-sheet behavior.
  const sortedSheetEvents = useMemo(() => {
    const copy = sheetEvents.filter(e =>
      e.travelType !== 'hotel'
      && e.travelType !== 'carservice'
      && e.travelType !== 'venue'
    );
    // Sort by full sheet-local DATETIME (date + time) derived from the
    // canonical UTC instant. Clock-time alone broke the case where a
    // late-night PT flight (e.g. 11:52 PM PT depart on May 20)
    // formatted as 02:52 ET on May 21 — the bare time put it at the
    // top of the May 20 list. Including the date pushes it after all
    // of May 20's events as expected.
    const localKey = (e) => {
      if (e.startInstant && sheetTimezone) {
        const d = formatInstantDateInZone(e.startInstant, sheetTimezone);
        const t = formatInstantInZone(e.startInstant, sheetTimezone);
        if (d && t) return `${d} ${t}`;
      }
      return `${e.date || '0000-00-00'} ${e.timeStart || '99:99'}`;
    };
    copy.sort((a, b) => {
      const aNd = a.nextDay ? 1 : 0;
      const bNd = b.nextDay ? 1 : 0;
      if (aNd !== bNd) return aNd - bNd;
      return localKey(a).localeCompare(localKey(b));
    });
    return copy;
  }, [sheetEvents, sheetTimezone]);

  // Group the sorted timeline into zone-band sections so multi-tz days
  // render with city/short-code headers instead of a single flat list.
  // Today + nextDay halves are grouped separately so the NEXT DAY
  // divider can sit between them — bands won't cross the day boundary.
  const timelineSections = useMemo(() => {
    const today = sortedSheetEvents.filter(e => !e.nextDay);
    const nextDay = sortedSheetEvents.filter(e => e.nextDay);
    return {
      today: groupEventsByZone(today, sheetTimezone),
      nextDay: groupEventsByZone(nextDay, sheetTimezone),
      hasNextDay: nextDay.length > 0,
    };
  }, [sortedSheetEvents, sheetTimezone]);

  // Lazy sheet creation — first add triggers POST /sheets, subsequent
  // adds use the cached id. Returns the resolved id or null on failure.
  async function ensureSheet() {
    if (sheetId) return sheetId;
    if (!artistId || !date) return null;
    setCreatingSheet(true);
    try {
      const artistIds = selArtistIds.length > 1 ? selArtistIds : undefined;
      const created = await api.createSheet({ artistId, artistIds, date, city: city || null, status: 'draft' });
      setSheetId(created.id);
      if (created.weather) setWeather(created.weather);
      return created.id;
    } catch (e) {
      alert('Could not create sheet: ' + (e.message || 'unknown'));
      return null;
    } finally {
      setCreatingSheet(false);
    }
  }

  async function addGcalEvent(ge) {
    const id = await ensureSheet();
    if (!id) return;
    setBusyKey(paletteSig('gcal', ge.gcalId));
    try {
      // Stamp the event with the artist's tz (the source zone for GCal
      // events — calendar.js formats incoming GCal datetimes in artist
      // tz before returning). On a foreign-tz sheet the renderer will
      // convert + show the indicator badge. Prefer the curated short
      // code; fall back to the raw IANA so zones without a 3-letter
      // abbreviation (Bogota, Lima, etc.) still round-trip through
      // Intl.DateTimeFormat without crashing the render.
      const artistIana = artists.find(a => a.id === artistId)?.timezone;
      const sourceTz = shortCodeForIana(artistIana) || artistIana || null;

      // Flight detection: when a GCal title looks like a flight, upgrade
      // the import to a real Travel record. The server's POST /travel
      // auto-generates the flight_departure + flight_arrival events with
      // proper airport-local timezones, which means: (a) the hero strip
      // lights up the EWR → LAS chip, (b) the band logic groups the legs
      // correctly, (c) the user can edit airline / passenger / seats
      // from a real travel record.
      //
      // Two title shapes we accept:
      //   "K2 Fly EWR-LAS (B6 0957)"                  ← IATA codes
      //   "Fly Buenos Aires to Panama City (CM 0328)" ← city names
      // Codes are direct. City names need a /travel/airports?q= lookup
      // to resolve to the metro hub airport — same lookup the Travel
      // form uses for its city → airport cascade.
      const t = (ge.title || '').trim();
      const iataMatch = t.match(/\b([A-Z]{3})\s*(?:[-→]|to)\s*([A-Z]{3})\b/);
      const cityMatch = !iataMatch
        && /\bfly\b/i.test(t)
        ? t.match(/\bfly\b\s+(.+?)\s+(?:to|→|-)\s+(.+?)(?:\s*\(|$)/i)
        : null;
      const flightCodeMatch = t.match(/\(([A-Z0-9]{1,3}\s*\d{1,4}[A-Z]?)\)/);
      const isFlightTitle = /\bfly\b/i.test(t) && (iataMatch || cityMatch);
      if (isFlightTitle) {
        let dep, arr;
        if (iataMatch) {
          dep = iataMatch[1].toUpperCase();
          arr = iataMatch[2].toUpperCase();
        } else {
          // City-name path: hit /travel/airports?q=<city> for each side
          // and take the top match's IATA. Same endpoint the Travel
          // form uses; results are deduped/ranked by metro hub.
          const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
          const token = localStorage.getItem('ds_token');
          const lookupCity = async (cityName) => {
            const q = (cityName || '').replace(/\s*city\s*$/i, '').trim();
            if (q.length < 2) return null;
            try {
              const r = await fetch(`${API_URL}/travel/airports?q=${encodeURIComponent(q)}`, {
                headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
              });
              const data = await r.json();
              const top = (data || [])[0];
              return top ? (top.iata || top.icao) : null;
            } catch { return null; }
          };
          [dep, arr] = await Promise.all([lookupCity(cityMatch[1]), lookupCity(cityMatch[2])]);
        }
        // City lookups can come back null when the metro doesn't have a
        // matching airports.json entry. Bail out of the Travel-record
        // upgrade in that case so the generic-event fallback below
        // still gives the user a flagged "flight" row.
        if (!dep || !arr) {
          // Skip Travel-record path; let the generic fallback handle it.
        } else {
        const flightCode = flightCodeMatch ? flightCodeMatch[1].replace(/\s+/g, '').toUpperCase() : null;
        const airlineCode = flightCode ? (flightCode.match(/^[A-Z]{1,3}/) || [null])[0] : null;
        const flightNumber = flightCode && airlineCode ? `${airlineCode}${flightCode.slice(airlineCode.length)}` : flightCode;
        // Pull leadPassenger from the title prefix when present —
        // "K2 Fly EWR-LAS" → "K2", "Joe Fly LAX-JFK" → "Joe". Keeps
        // the hero chip's initials matching how GCal labelled the trip.
        const passengerMatch = t.match(/^([A-Za-z][A-Za-z\s.]+?)\s+Fly\b/i);
        const leadPassenger = passengerMatch ? passengerMatch[1].trim() : null;
        try {
          const result = await api.createTravel({
            artistId,
            type: 'flight',
            flightType: 'commercial',
            startDate: date,
            endDate: date,
            departureAirport: dep,
            arrivalAirport: arr,
            flightNumber: flightNumber || null,
            flightDepartureTime: ge.timeStart || null,
            flightArrivalTime: ge.timeEnd || null,
            leadPassenger: leadPassenger || null,
            passenger: leadPassenger || null,
            // Tag the source GCal id on the travel record so we can
            // dedupe palette taps and (later) keep them in sync.
            gcalEventId: ge.gcalId || null,
            // Tell the server to stamp tz/instants on the events it
            // generates from the airport codes.
            createEvents: true,
            daysheetId: id,
          });
          // Server returns { record, events } — events have travelId
          // pointing at the new flight record. Link them to this sheet
          // and refresh local state.
          const newEvents = result?.events || [];
          for (const e of newEvents) {
            await api.linkEvent(e.id, id).catch(() => {});
          }
          const refreshed = await api.getEvents(id);
          setSheetEvents(prev => mergeEvents(prev, refreshed.events || []));
          // Refresh travel feed too so the new flight shows up in the
          // FLIGHTS palette + hero strip without a page reload.
          const fresh = await api.getAllTravel(artistId);
          setTravel(Array.isArray(fresh) ? fresh : (fresh?.travel || []));
          return;
        } catch (err) {
          // If the travel-record path fails for any reason, fall through
          // to the generic-event path below so the user still gets the
          // row on the sheet (just without the hero chip).
          console.warn('GCal → flight upgrade failed, falling back to generic event:', err?.message);
        }
        }
      }

      // Generic-event fallback (and the path for non-flight GCal items).
      // Sniff travel-type from common naming conventions so a flight that
      // didn't parse cleanly still gets the plane icon on the row. We
      // intentionally accept the "to"/"→"/"-" connectors here too so a
      // city-named flight whose airport lookup failed (no IATA match)
      // still shows up flagged as a flight rather than as a generic row.
      let travelType = null;
      if (/\bfly\b.*\b(?:to|→|-)\b/i.test(t)) {
        travelType = 'flight';
      } else if (/\bhotel\b|check[-\s]?in|check[-\s]?out/i.test(t)) {
        travelType = 'hotel';
      } else if (/\bcar service\b|\bpickup\b|\bdrop[-\s]?off\b/i.test(t)) {
        travelType = 'carservice';
      }
      const created = await api.createEvent({
        artistId,
        date,
        title: ge.title || 'Event',
        timeStart: ge.timeStart || null,
        timeEnd: ge.timeEnd || null,
        timezone: sourceTz || null,
        location: ge.location || null,
        notes: ge.description || null,
        gcalEventId: ge.gcalId || null,
        travelType,
        daysheetId: id,
      });
      setSheetEvents(prev => [...prev, created]);
    } catch (e) {
      alert('Could not add event: ' + (e.message || 'unknown'));
    } finally {
      setBusyKey(null);
    }
  }

  async function addTravelToSheet(record) {
    const id = await ensureSheet();
    if (!id) return;
    setBusyKey(paletteSig('travel', record.id));
    try {
      // Look up any events already tied to this travel record. The
      // server's regenerateEvents flag only creates events when NONE
      // exist; if the record has a partial set (e.g. arrival only),
      // we need to handle the missing half ourselves.
      const all = await api.getEventsByArtist(artistId);
      const linked = (all.events || []).filter(e => e.travelId === record.id);

      if (record.type === 'flight') {
        // Route through addFlightLeg so the title / notes format
        // (initials + route + flight code + greeter + passengers)
        // stays consistent with the per-leg sub-row clicks.
        const hasDep = linked.some(e => e.travelType === 'flight_departure');
        const hasArr = linked.some(e => e.travelType === 'flight_arrival');
        if (!hasDep) await addFlightLeg(record, 'departure');
        if (!hasArr) await addFlightLeg(record, 'arrival');
      }
      // Hotels and car services intentionally don't create a timeline
      // row — they live in the hero chip strip instead. Just having
      // the travel record exist (and matching the sheet's date range)
      // is enough for the strip to pick them up.

      // Link any pre-existing events that weren't already on this sheet.
      // Skip hotel/carservice events for the same reason as above.
      for (const e of linked) {
        if (e.travelType === 'hotel' || e.travelType === 'carservice') continue;
        await api.linkEvent(e.id, id).catch(() => {});
      }

      // Merge refreshed events with whatever's already in local
      // state — never wipe. If the server's eventIds list is
      // incomplete (orphan events from earlier daysheetId-less
      // creates), the disk view alone would drop them.
      const refreshed = await api.getEvents(id);
      setSheetEvents(prev => mergeEvents(prev, refreshed.events || []));
    } catch (e) {
      alert('Could not attach: ' + (e.message || 'unknown'));
    } finally {
      setBusyKey(null);
    }
  }

  // Click handler for a hero chip — open the appropriate editor.
  // Travel records (flight / hotel / carservice) reuse TravelFormModal
  // in edit mode; venue chips (which are events under the hood) open
  // the EventModal so the user gets the same rich edit surface as the
  // timeline rows.
  function openHeroChip(record) {
    if (record.type === 'venue') {
      const ev = sheetEvents.find(e => e.id === record._eventId);
      if (ev) setEditingEvent(ev);
      return;
    }
    if (record.type === 'flight' || record.type === 'hotel' || record.type === 'carservice') {
      setEditingTravel(record);
    }
  }

  // Delete a hero chip from the sheet.
  // - Travel records: delete the record outright (server cleans up
  //   any linked events). Same call the /admin/travel page uses.
  // - Venue chips: delete the underlying event (it's the only place
  //   the venue lives, since venues aren't travel records).
  async function deleteHeroChip(record) {
    if (record.type === 'venue') {
      const ev = sheetEvents.find(e => e.id === record._eventId);
      if (!ev) return;
      if (!window.confirm(`Delete venue "${ev.title || 'Venue'}"?`)) return;
      try {
        await api.deleteEvent(ev.id);
        setSheetEvents(prev => prev.filter(e => e.id !== ev.id));
        setAllVenues(prev => prev.filter(v => v.id !== ev.id));
      } catch (e) {
        alert('Could not delete venue: ' + (e.message || 'unknown'));
      }
      return;
    }
    const label = record.type === 'flight'
      ? `flight ${[record.airline, record.flightNumber].filter(Boolean).join(' ') || ''}`.trim()
      : record.type === 'hotel'
        ? `hotel "${record.hotelName || 'Hotel'}"`
        : `car service`;
    if (!window.confirm(`Delete this ${label}?`)) return;
    try {
      await api.deleteTravel(record.id);
      // Refresh travel + sheet events (the server cleans up any
      // events that were linked to this travel record).
      const fresh = await api.getAllTravel(artistId);
      setTravel(Array.isArray(fresh) ? fresh : (fresh?.travel || []));
      if (sheetId) {
        const ev = await api.getEvents(sheetId);
        setSheetEvents(prev => mergeEvents(prev, ev.events || []));
      }
    } catch (e) {
      alert('Could not delete: ' + (e.message || 'unknown'));
    }
  }

  async function removeEventFromSheet(eventId) {
    if (!sheetId) return;
    try {
      await api.unlinkEvent(eventId, sheetId);
      setSheetEvents(prev => prev.filter(e => e.id !== eventId));
    } catch (e) {
      alert('Could not remove: ' + (e.message || 'unknown'));
    }
  }

  // After TravelFormModal saves a record, the server returns the new
  // record plus its auto-generated events. Flights link onto the
  // timeline (departure + arrival rows); hotels and car services stay
  // off the timeline and surface as chips in the hero strip instead —
  // matches the published-sheet behavior.
  async function handleTravelModalSave(result) {
    const savedType = result?.record?.type || result?.type || travelModalType;
    setTravelModalType(null);
    const id = await ensureSheet();
    if (!id) return;
    try {
      if (result?.events?.length && savedType !== 'hotel' && savedType !== 'carservice') {
        for (const evt of result.events) {
          if (evt.id) await api.linkEvent(evt.id, id).catch(() => {});
        }
      }
      // Refresh travel + sheet events
      const fresh = await api.getAllTravel(artistId);
      setTravel(Array.isArray(fresh) ? fresh : (fresh?.travel || []));
      const ev = await api.getEvents(id);
      setSheetEvents(prev => mergeEvents(prev, ev.events || []));
    } catch (e) {
      alert('Could not attach travel: ' + (e.message || 'unknown'));
    }
  }

  async function createEvent(payload) {
    setNewEventOpen(false);
    const id = await ensureSheet();
    if (!id) return;
    try {
      // Pass through whatever fields the modal provided (title, person,
      // date, timeStart/End, timezone, location, notes, url, files,
      // contacts, travelType) so the rich SheetBuilder EventModal works
      // identically here.
      const created = await api.createEvent({
        artistId,
        date,
        ...payload,
        daysheetId: id,
      });
      setSheetEvents(prev => [...prev, created]);
    } catch (e) {
      alert('Could not create event: ' + (e.message || 'unknown'));
    }
  }

  // Save a venue from VenueModal as an event tagged travelType='venue'
  // so it lands in the hero chip strip (not on the timeline). Tier-1
  // venue fields (soundcheck / doors / showtime / capacity / production
  // contact) are stitched into the event's notes block since the event
  // schema doesn't have native columns for them — same shape the rest
  // of the app uses for travel-related event notes.
  async function createVenue(payload) {
    setVenueModalOpen(false);
    const id = await ensureSheet();
    if (!id) return;
    // Notes display in 12-hour time so the venue card reads the same
    // way times do everywhere else in the app.
    // Each opener is its own line so the email's notes parser turns it
     // into a separate bold "OPENER:" row. Bill order is preserved
     // (array order == top → main support).
    const openerLines = (payload.openers || [])
      .filter(o => (o.name || '').trim())
      .map(o => `Opener: ${o.name.trim()}${o.time ? ` · ${formatTime(o.time)}` : ''}`);
    const noteLines = [
      payload.address ? payload.address : null,
      payload.capacity ? `Capacity: ${payload.capacity}` : null,
      payload.soundcheck ? `Soundcheck: ${formatTime(payload.soundcheck)}` : null,
      payload.doors ? `Doors: ${formatTime(payload.doors)}` : null,
      ...openerLines,
      payload.showtime ? `Showtime: ${formatTime(payload.showtime)}` : null,
      payload.contactName ? `Production: ${payload.contactName}${payload.contactPhone ? ` · ${payload.contactPhone}` : ''}` : null,
    ].filter(Boolean);
    // Resolve the venue's timezone from its placeId so the auto-event
    // sits in the right zone band on multi-tz days (Madison Square
    // Garden → ET, Movistar Arena → ART). Best-effort — falls back to
    // the sheet tz inheritance when the place lookup fails.
    let venueTz = null;
    if (payload.placeId) {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const token = localStorage.getItem('ds_token');
        const r = await fetch(`${API_URL}/places/details?placeId=${encodeURIComponent(payload.placeId)}`, {
          headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
        });
        const data = await r.json();
        if (data?.iana) venueTz = shortCodeForIana(data.iana) || data.iana;
      } catch { /* ignore — non-fatal */ }
    }
    try {
      const created = await api.createEvent({
        artistId,
        date,
        title: payload.name,
        // Use showtime as the sortable timeStart so the venue chip can
        // also stand in chronologically if we ever surface it on the
        // timeline. Falls back to doors → soundcheck → null.
        timeStart: payload.showtime || payload.doors || payload.soundcheck || null,
        timezone: venueTz,
        location: payload.address || null,
        notes: noteLines.join('\n') || null,
        travelType: 'venue',
        // Captured from autocomplete — drives the iOS venue detail
        // card's photo / website / phone / hours enrichment via
        // /places/details. nil when the user didn't pick a prediction.
        placeId: payload.placeId || null,
        daysheetId: id,
      });
      setSheetEvents(prev => [...prev, created]);
      // Surface the new venue in the palette catalog right away so
      // it can be re-attached / cloned without a page refresh. Slot
      // events (soundcheck/doors/showtime) aren't pre-created — they
      // get parsed from the venue's notes and the user adds them
      // individually from the palette.
      setAllVenues(prev => [...prev, created]);
      setAllArtistEvents(prev => [...prev, created]);
    } catch (e) {
      alert('Could not create venue: ' + (e.message || 'unknown'));
    }
  }

  // Add a single flight leg (departure OR arrival) to the sheet —
  // mirror of the venue slot flow so users can pick which legs land
  // on the timeline instead of always getting both. The hero chip
  // strip auto-shows the flight as soon as either leg is attached.
  //
  // Title format:
  //   Departure → "{lead initials} Fly {DEP}-{ARR} ({airline} {flight#})"
  //   Arrival   → "{lead initials} arrives {ARR} ({airline} {flight#})"
  // Notes carry greeter contact + the passenger list so the rendered
  // sheet shows the full context under the title.
  async function addFlightLeg(flight, leg) {
    const id = await ensureSheet();
    if (!id) return;
    const isDeparture = leg === 'departure';
    const pax = (flight.passenger || '').split(',').map(p => p.trim()).filter(Boolean);
    const lead = flight.leadPassenger || pax[0] || '';
    const initials = lead ? resolveInitials(lead, artists) : '';
    const code = [flight.airline, flight.flightNumber].filter(Boolean).join(' ');
    const codeSuffix = code ? ` (${code})` : '';
    const title = isDeparture
      ? `${initials ? initials + ' ' : ''}Fly ${flight.departureAirport || '?'}-${flight.arrivalAirport || '?'}${codeSuffix}`.trim()
      : `${initials ? initials + ' ' : ''}arrives ${flight.arrivalAirport || '?'}${codeSuffix}`.trim();
    // Build notes: Greeter line + Passengers line (joined naturally).
    const noteLines = [];
    if (flight.greeterName || flight.greeterPhone) {
      noteLines.push(`Greeter: ${[flight.greeterName, flight.greeterPhone].filter(Boolean).join(': ')}`);
    }
    if (pax.length) {
      // "K2 and George" / "K2, George, and Joe" — natural joining for
      // the passenger list, falling back to a comma list when long.
      let paxLine = '';
      if (pax.length === 1) paxLine = pax[0];
      else if (pax.length === 2) paxLine = `${pax[0]} and ${pax[1]}`;
      else paxLine = `${pax.slice(0, -1).join(', ')}, and ${pax[pax.length - 1]}`;
      noteLines.push(`Passengers: ${paxLine}`);
    }
    try {
      const created = await api.createEvent({
        artistId,
        date: isDeparture
          ? (flight.startDate || date)
          : (flight.endDate || flight.startDate || date),
        title,
        timeStart: isDeparture ? (flight.flightDepartureTime || null) : (flight.flightArrivalTime || null),
        location: isDeparture
          ? (flight.departureAirport ? `${flight.departureAirport} Airport` : null)
          : (flight.arrivalAirport ? `${flight.arrivalAirport} Airport` : null),
        notes: noteLines.length ? noteLines.join('\n') : null,
        travelId: flight.id,
        travelType: isDeparture ? 'flight_departure' : 'flight_arrival',
        daysheetId: id,
      });
      if (created?.id) await api.linkEvent(created.id, id).catch(() => {});
      setSheetEvents(prev => [...prev, created]);
    } catch (e) {
      alert('Could not add flight leg: ' + (e.message || 'unknown'));
    }
  }

  // Add a single car-service leg (pickup OR dropoff) as a timeline
  // event. Pickup carries the car's scheduled time; dropoff is
  // time-less so it sorts to end-of-day until the user retimes it.
  // Title format: "{primary artist} depart for {short dest}" /
  // "{primary artist} arrive at {short dest}" so the row reads from
  // the passenger's perspective. The full address lives in `location`
  // and shows beneath the title as the clickable detail line.
  async function addCarLeg(car, leg) {
    const id = await ensureSheet();
    if (!id) return;
    const isPickup = leg === 'pickup';
    const primaryArtist = artists.find(a => a.id === artistId);
    const who = primaryArtist?.name?.split(' ')[0] || 'Artist';
    const fullDest = car.carServiceDropoff || car.carServicePickup || '';
    // Shorten "JFK Airport, Queens, NY 11430" → "JFK Airport" by
    // taking the first comma-separated segment. Keeps the title
    // scannable; the full address stays in `location`.
    const shortDest = fullDest.split(',')[0].trim();
    // Driver-action verbs (pick up / drop off) so it doesn't confuse
    // with the flight depart / arrive titles.
    const title = shortDest
      ? (isPickup ? `${who} pick up for ${shortDest}` : `${who} drop off at ${shortDest}`)
      : (isPickup ? `${who} pick up` : `${who} drop off`);
    try {
      // No `person` set — the sheet's primary artist is implied,
      // and the user can toggle a who in the EventModal if they
      // want explicit tagging.
      const created = await api.createEvent({
        artistId,
        date,
        title,
        timeStart: isPickup ? (car.carServiceTime || null) : null,
        location: isPickup ? (car.carServicePickup || null) : (car.carServiceDropoff || null),
        travelId: car.id,
        travelType: isPickup ? 'carservice_pickup' : 'carservice_dropoff',
        daysheetId: id,
      });
      if (created?.id) await api.linkEvent(created.id, id).catch(() => {});
      setSheetEvents(prev => [...prev, created]);
    } catch (e) {
      alert('Could not add car leg: ' + (e.message || 'unknown'));
    }
  }

  async function togglePublish() {
    if (!sheetId) {
      alert('Add at least one item to the sheet first.');
      return;
    }
    setPublishing(true);
    try {
      if (sheetStatus === 'published') {
        await api.unpublishSheet(sheetId);
        setSheetStatus('draft');
      } else {
        await api.publishSheet(sheetId);
        setSheetStatus('published');
      }
    } catch (e) {
      alert('Could not change status: ' + (e.message || 'unknown'));
    } finally {
      setPublishing(false);
    }
  }

  // Open the send modal — passes the sheet plus the artist record
  // (SendModal needs distributionContacts / distributionGroups off
  // the artist) shaped the way SendModal expects.
  function openSendModal() {
    if (!sheetId) {
      alert('Add at least one item to the sheet first.');
      return;
    }
    setSendOpen(true);
  }

  // AI-extract from a dropped/picked file. Calls /sheets/extract-multi
  // which runs Claude + Gemini + OpenAI in parallel and reconciles
  // disagreements via OpenAI before returning a single canonical
  // {travel, events} payload. The response includes a `providers`
  // array showing which models contributed — surfaced in the status
  // line so you can see "extracted by claude + gemini" at a glance.
  async function handleImportFile(file) {
    const id = await ensureSheet();
    if (!id) return;
    setImporting(true);
    setImportMsg('Reading file…');
    try {
      const dataUrl = await new Promise(r => {
        const fr = new FileReader();
        fr.onload = () => r(fr.result);
        fr.readAsDataURL(file);
      });
      const base64 = dataUrl.split(',')[1];
      const mimeType = file.type || 'application/pdf';
      setImportMsg('Extracting (Claude + Gemini + GPT)…');
      const out = await apiFetch('/sheets/extract-multi', {
        method: 'POST',
        body: JSON.stringify({ base64, mimeType, fileName: file.name }),
      });
      const extractedTravel = Array.isArray(out.travel) ? out.travel : [];
      const extractedEvents = Array.isArray(out.events) ? out.events : [];
      // Persist travel records (don't auto-attach — the user picks
      // legs from the palette like a normal flight/car).
      const newTravel = [];
      for (const t of extractedTravel) {
        try {
          const created = await api.createTravel({ ...t, artistId });
          newTravel.push(created);
        } catch {}
      }
      if (newTravel.length) setTravel(prev => [...prev, ...newTravel]);
      // Persist extracted events directly to today's sheet.
      const newEvents = [];
      for (const ev of extractedEvents) {
        try {
          const created = await api.createEvent({
            artistId,
            date: ev.date || date,
            title: ev.title || ev.summary || 'Event',
            timeStart: ev.timeStart || ev.startTime || null,
            timeEnd: ev.timeEnd || ev.endTime || null,
            location: ev.location || null,
            notes: ev.notes || ev.description || null,
            daysheetId: id,
          });
          newEvents.push(created);
        } catch {}
      }
      if (newEvents.length) setSheetEvents(prev => [...prev, ...newEvents]);
      const summary = [
        newTravel.length ? `${newTravel.length} travel record${newTravel.length === 1 ? '' : 's'}` : null,
        newEvents.length ? `${newEvents.length} event${newEvents.length === 1 ? '' : 's'}` : null,
      ].filter(Boolean).join(' · ');
      const provLabel = (out.providers || []).join(' + ') || 'AI';
      setImportMsg(summary
        ? `Imported ${summary} via ${provLabel}`
        : `${provLabel} found nothing in that file.`);
      setTimeout(() => setImportMsg(''), 5000);
    } catch (e) {
      setImportMsg('Failed: ' + (e.message || 'unknown'));
      setTimeout(() => setImportMsg(''), 6000);
    } finally {
      setImporting(false);
    }
  }

  // Promote a parsed-from-notes slot into a real timeline event on
  // today's sheet. Slot has shape { label, time, title }; venue
  // contributes the address as the event's location.
  async function addVenueSlotToSheet(slot, venue) {
    const id = await ensureSheet();
    if (!id) return;
    try {
      const created = await api.createEvent({
        artistId,
        date,
        title: slot.title,
        timeStart: slot.time,
        location: venue.location || null,
        daysheetId: id,
      });
      setSheetEvents(prev => [...prev, created]);
      setAllArtistEvents(prev => [...prev, created]);
    } catch (e) {
      alert('Could not add: ' + (e.message || 'unknown'));
    }
  }

  // Link an existing venue event (already on this date) to the
  // current sheet — same idea as how addTravelToSheet links a
  // pre-existing flight event onto a new sheet.
  async function attachVenueToSheet(venue) {
    const id = await ensureSheet();
    if (!id) return;
    try {
      await api.linkEvent(venue.id, id);
      const ev = await api.getEvents(id);
      setSheetEvents(prev => mergeEvents(prev, ev.events || []));
    } catch (e) {
      alert('Could not attach venue: ' + (e.message || 'unknown'));
    }
  }

  // Clone a venue chip from another date onto today's sheet. Timing
  // slots (soundcheck / doors / showtime) stay in the palette as
  // individually-addable items rather than auto-attaching, so the
  // user picks what lands on this day.
  async function cloneVenueToSheet(template) {
    const id = await ensureSheet();
    if (!id) return;
    try {
      const created = await api.createEvent({
        artistId,
        date,
        title: template.title || 'Venue',
        timeStart: template.timeStart || null,
        location: template.location || null,
        notes: template.notes || null,
        travelType: 'venue',
        daysheetId: id,
      });
      setSheetEvents(prev => [...prev, created]);
      setAllVenues(prev => [...prev, created]);
      setAllArtistEvents(prev => [...prev, created]);
    } catch (e) {
      alert('Could not clone venue: ' + (e.message || 'unknown'));
    }
  }

  // PATCH an existing timeline event with the EventModal payload,
  // then replace the local row with the server response. Replacing
  // (vs spreading over the old row) ensures cleared fields like
  // `person: null` actually clear — a spread would leave the previous
  // value in place if the server omitted the key.
  async function updateEvent(eventId, payload) {
    setEditingEvent(null);
    try {
      const updated = await api.updateEvent(eventId, payload);
      setSheetEvents(prev => prev.map(e => e.id === eventId ? { ...updated } : e));
    } catch (e) {
      alert('Could not update event: ' + (e.message || 'unknown'));
    }
  }

  // Mark / unmark an existing sheet event as next-day. PATCH the row
  // and reflect locally; the timeline re-sorts via the memo above.
  async function toggleNextDay(eventId, currentValue) {
    try {
      await api.updateEvent(eventId, { nextDay: !currentValue });
      setSheetEvents(prev => prev.map(e => e.id === eventId ? { ...e, nextDay: !currentValue } : e));
    } catch (e) {
      alert('Could not flip next-day: ' + (e.message || 'unknown'));
    }
  }

  // Already-attached travel ids so we can dim travel rows that are
  // already represented on the sheet.
  const attachedTravelIds = useMemo(() => {
    return new Set(sheetEvents.map(e => e.travelId).filter(Boolean));
  }, [sheetEvents]);

  // Hero strip — chips at the top of the right panel that mirror the
  // published sheet's travel-icon row. A travel record qualifies if
  // (a) it has any event linked to this sheet, or (b) it's a hotel
  // whose stay range covers this date (passive lodging — no event row).
  const heroChips = useMemo(() => {
    if (!date) return [];
    const inDateRange = (t) => {
      if (t.startDate === date) return true;
      if (t.endDate === date) return true;
      if (t.startDate && t.endDate && t.startDate <= date && date <= t.endDate) return true;
      return false;
    };
    return travel.filter(t => {
      // Flights & other typed records show as chips when they have
      // events on the sheet (admin explicitly attached them).
      if (attachedTravelIds.has(t.id)) return true;
      // Hotels and car services are chip-only — no timeline rows. Show
      // them whenever they touch this sheet's date.
      if ((t.type === 'hotel' || t.type === 'carservice') && inDateRange(t)) return true;
      return false;
    });
  }, [travel, attachedTravelIds, date]);

  // Already-attached gcal ids so we don't double-add a calendar event.
  const attachedGcalIds = useMemo(() => {
    return new Set(sheetEvents.map(e => e.gcalEventId).filter(Boolean));
  }, [sheetEvents]);

  // Palette is scoped to the selected date — only show pre-existing
  // travel that actually touches this day so the rail isn't flooded
  // with unrelated trips.
  //   flight:     departs OR arrives on this date (covers overnights)
  //   hotel:      stay range covers this date
  //   carservice: same-day pickup
  const matchesDate = (t) => {
    if (!date) return false;
    if (t.startDate === date) return true;
    if (t.endDate === date) return true;
    if (t.startDate && t.endDate && t.startDate <= date && t.endDate >= date) return true;
    return false;
  };
  const flights = travel.filter(t => t.type === 'flight' && matchesDate(t));
  const hotels = travel.filter(t => t.type === 'hotel' && matchesDate(t));
  const carServices = travel.filter(t => t.type === 'carservice' && matchesDate(t));
  // Only surface venues that belong to the current date — same rule
  // hotels follow (they only appear when their stay covers today).
  // Venues from other dates aren't auto-listed; the user re-creates
  // or imports them via the VENUE button when they want one.
  const venueCatalog = useMemo(() => {
    const map = new Map();
    for (const v of allVenues) {
      if (v.date !== date) continue;
      const key = `${(v.title || '').trim().toLowerCase()}|${(v.location || '').trim().toLowerCase()}`;
      if (!map.has(key)) map.set(key, v);
    }
    return Array.from(map.values());
  }, [allVenues, date]);
  // For each venue, parse soundcheck / doors / showtime times out of
  // its notes block. Returns virtual slot descriptors — each is
  // promoted to a real event only when the user clicks "+" to attach.
  // An existing event on the current sheet at that time + title pattern
  // counts as already-attached so the chip flips to ✓.
  function timingSlotsFor(venue) {
    const parsed = parseVenueSlots(venue.notes);
    return parsed.map(s => {
      const title = `${s.label} — ${venue.title}`;
      const attached = sheetEvents.some(e =>
        e.title === title && e.timeStart === s.time
      );
      return { ...s, title, attached };
    });
  }

  // Build a drag payload for a palette item so it can be dropped into
  // the note card. Same shape used by timeline rows so NoteCard's drop
  // handler can stay generic (title / timeStart / location).
  const startDragFromPalette = (e, payload) => {
    e.dataTransfer.setData('application/x-daysheets-event', JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth: 1400, margin: '0 auto', paddingBottom: isMobile ? 'calc(24px + env(safe-area-inset-bottom))' : 24 }}>
      <div style={{ marginBottom: isMobile ? 14 : 20 }}>
        <h1 style={{ margin: 0, fontSize: isMobile ? 20 : 24, fontWeight: 800 }}>Sheet Builder (new)</h1>
        <p style={{ margin: '6px 0 0', color: 'var(--text3)', fontSize: isMobile ? 12 : 13 }}>
          {isMobile
            ? 'Tap items in the palette to assemble your day sheet. Flights split into departure + arrival.'
            : 'Click items in the left palette to assemble a day sheet on the right. Flights split into departure + arrival.'}
        </p>
      </div>

      {/* Two-column on desktop, stacked on mobile so the palette and
          timeline are both fully readable on a phone. Mobile order is
          palette → timeline so admins set the artist + date first, then
          scroll down to see the assembling sheet. */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(260px, 1fr) 2fr', gap: isMobile ? 16 : 24, alignItems: 'start' }}>
        {/* LEFT — artist picker, date/location, palette */}
        <div>
          {/* Artist circles — multi-select for joint sheets. */}
          <div style={SECTION}>
            <label style={SECTION_LABEL}>Artist</label>
            <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 4 }}>
              {artists.map(a => {
                const selected = selArtistIds.includes(a.id);
                const isPrimary = a.id === artistId;
                const color = a.color || '#1E40AF';
                return (
                  <button
                    key={a.id}
                    onClick={() => {
                      if (selected && selArtistIds.length > 1) {
                        const newIds = selArtistIds.filter(id => id !== a.id);
                        setSelArtistIds(newIds);
                        if (isPrimary) setArtistId(newIds[0]);
                      } else if (!selected) {
                        const newIds = [...selArtistIds, a.id];
                        setSelArtistIds(newIds);
                        if (!artistId) setArtistId(a.id);
                      }
                    }}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, minWidth: 68, flexShrink: 0 }}
                  >
                    <div style={{ position: 'relative', width: 60, height: 60 }}>
                      <div style={{ width: 60, height: 60, borderRadius: '50%', overflow: 'hidden', background: `${color}20`, border: selected ? `2.5px solid ${color}` : '2.5px solid transparent', transition: 'all 0.15s', opacity: selected ? 1 : 0.4 }}>
                        {a.photo ? (
                          <img src={a.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: '100%', height: '100%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 800, color: '#fff' }}>
                            {a.emoji || a.name?.charAt(0)}
                          </div>
                        )}
                      </div>
                      {selected && (
                        <div style={{ position: 'absolute', bottom: -2, right: -2, width: 20, height: 20, borderRadius: '50%', background: '#22c55e', border: '2.5px solid var(--bg2)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                          <i className="fa-solid fa-check" style={{ fontSize: 9, color: '#fff' }}></i>
                        </div>
                      )}
                    </div>
                    <span style={{ fontSize: 11, fontWeight: selected ? 700 : 500, color: selected ? 'var(--text)' : 'var(--text3)', whiteSpace: 'nowrap', maxWidth: 68, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {a.name?.split(' ')[0]}
                    </span>
                  </button>
                );
              })}
            </div>
            {selArtistIds.length > 1 && (() => {
              // Tint the joint pill with the same multi-color gradient
              // used on the preview's accent strip so the picker and
              // the rendered sheet read as the same group.
              const colors = selArtistIds.map(id => artists.find(a => a.id === id)?.color || '#1E40AF');
              const gradient = `linear-gradient(to right, ${colors.join(', ')})`;
              return (
                <div style={{ marginTop: 10, borderRadius: 10, padding: 1.5, background: gradient, display: 'inline-flex' }}>
                  <div style={{ padding: '7px 13px', borderRadius: 9, background: 'var(--bg2)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <i className="fa-solid fa-users" style={{ fontSize: 12, color: colors[0] }}></i>
                    <span style={{ fontSize: 13, fontWeight: 700, background: gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Joint Day Sheet</span>
                    <span style={{ fontSize: 12, color: 'var(--text2)' }}>
                      — {selArtistIds.map(id => artists.find(a => a.id === id)?.name?.split(' ')[0]).filter(Boolean).join(' & ')}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Date + starting location — stacked. */}
          <div style={{ ...SECTION, position: 'relative', zIndex: 60 }}>
            <div style={{ position: 'relative', zIndex: 61, marginBottom: 12 }}>
              <label style={SECTION_LABEL}>Date</label>
              <CalendarPicker value={date} onChange={setDate} />
            </div>
            <div style={{ position: 'relative', zIndex: 60 }}>
              <label style={SECTION_LABEL}>Starting location</label>
              <LocationSearch value={city} onChange={setCity} />
            </div>
          </div>
          {/* Quick-create — tiles styled like the tCard chips in
              SheetBuilder's preview rail, so each palette tile
              previews exactly what its chip will look like on the
              rendered sheet. */}
          <div style={SECTION}>
            <div style={SECTION_LABEL}>Add new</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              <button
                style={tileStyle('#1E40AF')}
                onClick={() => { setEventSeed(null); setNewEventOpen(true); }}
                title="Add event"
              >
                <i className="fa-solid fa-calendar-plus" style={{ fontSize: 18 }}></i>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.04em' }}>EVENT</span>
              </button>
              <button
                style={tileStyle('#1E40AF')}
                onClick={() => setTravelModalType('flight')}
                title="New flight"
              >
                <i className="fa-solid fa-plane" style={{ fontSize: 18 }}></i>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.04em' }}>FLIGHT</span>
              </button>
              <button
                style={tileStyle('#06B6D4')}
                onClick={() => setTravelModalType('hotel')}
                title="New hotel"
              >
                <i className="fa-solid fa-hotel" style={{ fontSize: 18 }}></i>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.04em' }}>HOTEL</span>
              </button>
              <button
                style={tileStyle('#06B6D4')}
                onClick={() => setTravelModalType('carservice')}
                title="New car service"
              >
                <i className="fa-solid fa-car-side" style={{ fontSize: 18 }}></i>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.04em' }}>CAR</span>
              </button>
              <button
                style={tileStyle('#dc2626')}
                onClick={() => setVenueModalOpen(true)}
                title="New venue"
              >
                <i className="fa-solid fa-landmark-dome" style={{ fontSize: 18 }}></i>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.04em' }}>VENUE</span>
              </button>
              <button
                style={tileStyle('#f59e0b')}
                onClick={async () => { await ensureSheet(); setShowNoteCard(true); }}
                title="Add note"
              >
                <i className="fa-solid fa-note-sticky" style={{ fontSize: 18 }}></i>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.04em' }}>NOTE</span>
              </button>
            </div>
          </div>

          {/* AI import — drop a rider / itinerary / confirmation
              email PDF to auto-extract travel + events. Routes
              travel-looking docs through Claude (best for structured
              JSON), everything else through Gemini (best for
              long/messy docs and multimodal). */}
          <div style={SECTION}>
            <div style={SECTION_LABEL}>Import with AI</div>
            <label
              style={{
                display: 'block',
                padding: '14px 12px',
                borderRadius: 12,
                border: `1.5px dashed ${importing ? '#a855f7' : 'var(--border)'}`,
                background: importing ? 'rgba(168,85,247,0.06)' : 'transparent',
                textAlign: 'center',
                cursor: importing ? 'progress' : 'pointer',
                transition: 'all 0.15s',
              }}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#a855f7'; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = importing ? '#a855f7' : 'var(--border)'; }}
              onDrop={async e => {
                e.preventDefault();
                if (importing) return;
                const f = e.dataTransfer.files?.[0];
                if (f) await handleImportFile(f);
              }}
            >
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.eml,.txt,image/*,application/pdf"
                style={{ display: 'none' }}
                disabled={importing}
                onChange={async e => { const f = e.target.files?.[0]; if (f) await handleImportFile(f); e.target.value = ''; }}
              />
              <div style={{ fontSize: 13, fontWeight: 700, color: importing ? '#a855f7' : 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <i className={`fa-solid ${importing ? 'fa-wand-magic-sparkles' : 'fa-file-import'}`} style={{ fontSize: 13 }}></i>
                {importing ? 'Working…' : 'Drop a PDF / itinerary / email'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                Auto-extracts flights, hotels, cars, and events
              </div>
            </label>
            {importMsg && (
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 8, padding: '6px 10px', borderRadius: 8, background: 'var(--bg2)', border: '1px solid var(--border)' }}>
                {importMsg}
              </div>
            )}
          </div>

          {/* GCal */}
          <div style={SECTION}>
            <div style={SECTION_LABEL}>Google Calendar — {date}</div>
            {gcalEvents.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text3)', padding: '8px 4px' }}>No GCal events for this date.</div>
            )}
            {gcalEvents.map(ge => {
              const sig = paletteSig('gcal', ge.gcalId);
              const attached = attachedGcalIds.has(ge.gcalId);
              return (
                <button
                  key={ge.gcalId || ge.title}
                  style={{ ...PALETTE_BTN, opacity: attached ? 0.5 : 1, cursor: 'grab' }}
                  disabled={attached || busyKey === sig}
                  draggable
                  onDragStart={(e) => startDragFromPalette(e, {
                    title: ge.title || 'Event',
                    timeStart: ge.timeStart || null,
                    location: ge.location || null,
                  })}
                  onClick={() => addGcalEvent(ge)}
                >
                  <span style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{ge.title || '(untitled)'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                      {formatTime(ge.timeStart)}{ge.location ? ` · ${ge.location}` : ''}
                    </div>
                  </span>
                  <i className={`fa-solid ${attached ? 'fa-check' : 'fa-plus'}`} style={{ fontSize: 11, color: attached ? '#22c55e' : 'var(--text3)' }}></i>
                </button>
              );
            })}
          </div>

          {/* Travel records */}
          {flights.length > 0 && (
            <div style={SECTION}>
              <div style={SECTION_LABEL}>Flights</div>
              {flights.map(f => {
                const sig = paletteSig('travel', f.id);
                const depAttached = sheetEvents.some(e => e.travelId === f.id && e.travelType === 'flight_departure');
                const arrAttached = sheetEvents.some(e => e.travelId === f.id && e.travelType === 'flight_arrival');
                const bothAttached = depAttached && arrAttached;
                return (
                  <div key={f.id} style={{ marginBottom: 8 }}>
                    <div style={{ display:'flex', alignItems:'stretch', gap:6, marginBottom: 4 }}>
                      <button
                        style={{ ...PALETTE_BTN, opacity: bothAttached ? 0.5 : 1, cursor: 'grab', marginBottom: 0, flex: 1 }}
                        disabled={bothAttached || busyKey === sig}
                        draggable
                        onDragStart={(e) => startDragFromPalette(e, {
                          title: `Flight ${[f.airline, f.flightNumber].filter(Boolean).join(' ')}`.trim(),
                          timeStart: f.flightDepartureTime || null,
                          location: `${f.departureAirport || '?'} → ${f.arrivalAirport || '?'}`,
                        })}
                        onClick={() => addTravelToSheet(f)}
                        title="Add both departure + arrival"
                      >
                        <span style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600 }}>{f.airline || ''} {f.flightNumber || ''}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                            {f.departureAirport || '?'} → {f.arrivalAirport || '?'} · {f.startDate || ''}
                          </div>
                        </span>
                        <i className={`fa-solid ${bothAttached ? 'fa-check' : 'fa-plus'}`} style={{ fontSize: 11, color: bothAttached ? '#22c55e' : 'var(--text3)' }}></i>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteHeroChip(f); }}
                        title="Permanently delete this flight (and its events)"
                        style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, padding:'0 12px', cursor:'pointer', color:'#dc2626', display:'flex', alignItems:'center', justifyContent:'center' }}
                      >
                        <i className="fa-solid fa-trash-can" style={{ fontSize: 12 }}></i>
                      </button>
                    </div>
                    <button
                      style={{ ...PALETTE_BTN, marginLeft: 16, padding: '8px 12px', opacity: depAttached ? 0.5 : 1 }}
                      disabled={depAttached}
                      onClick={() => addFlightLeg(f, 'departure')}
                    >
                      <span style={{ flex: 1, fontSize: 12 }}>
                        <span style={{ fontWeight: 600 }}>Departure</span>
                        {f.flightDepartureTime && <span style={{ color: 'var(--text3)', marginLeft: 8 }}>{formatTime(f.flightDepartureTime)}</span>}
                        {f.departureAirport && <span style={{ color: 'var(--text3)', marginLeft: 6 }}>· {f.departureAirport}</span>}
                      </span>
                      <i className={`fa-solid ${depAttached ? 'fa-check' : 'fa-plus'}`} style={{ fontSize: 10, color: depAttached ? '#22c55e' : 'var(--text3)' }}></i>
                    </button>
                    <button
                      style={{ ...PALETTE_BTN, marginLeft: 16, padding: '8px 12px', opacity: arrAttached ? 0.5 : 1 }}
                      disabled={arrAttached}
                      onClick={() => addFlightLeg(f, 'arrival')}
                    >
                      <span style={{ flex: 1, fontSize: 12 }}>
                        <span style={{ fontWeight: 600 }}>Arrival</span>
                        {f.flightArrivalTime && <span style={{ color: 'var(--text3)', marginLeft: 8 }}>{formatTime(f.flightArrivalTime)}</span>}
                        {f.arrivalAirport && <span style={{ color: 'var(--text3)', marginLeft: 6 }}>· {f.arrivalAirport}</span>}
                      </span>
                      <i className={`fa-solid ${arrAttached ? 'fa-check' : 'fa-plus'}`} style={{ fontSize: 10, color: arrAttached ? '#22c55e' : 'var(--text3)' }}></i>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {hotels.length > 0 && (
            <div style={SECTION}>
              <div style={SECTION_LABEL}>Hotels</div>
              {hotels.map(h => {
                const sig = paletteSig('travel', h.id);
                const attached = attachedTravelIds.has(h.id);
                return (
                  <div key={h.id} style={{ display:'flex', alignItems:'stretch', gap:6, marginBottom: 6 }}>
                    <button
                      style={{ ...PALETTE_BTN, opacity: attached ? 0.5 : 1, cursor: 'grab', marginBottom: 0, flex: 1 }}
                      disabled={attached || busyKey === sig}
                      draggable
                      onDragStart={(e) => startDragFromPalette(e, {
                        title: `Hotel: ${h.hotelName || 'Hotel'}`,
                        timeStart: null,
                        location: h.hotelAddress || null,
                      })}
                      onClick={() => addTravelToSheet(h)}
                    >
                      <span style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{h.hotelName || 'Hotel'}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>{h.startDate} → {h.endDate}</div>
                      </span>
                      <i className={`fa-solid ${attached ? 'fa-check' : 'fa-plus'}`} style={{ fontSize: 11, color: attached ? '#22c55e' : 'var(--text3)' }}></i>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteHeroChip(h); }}
                      title="Permanently delete this hotel"
                      style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, padding:'0 12px', cursor:'pointer', color:'#dc2626', display:'flex', alignItems:'center', justifyContent:'center' }}
                    >
                      <i className="fa-solid fa-trash-can" style={{ fontSize: 12 }}></i>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {carServices.length > 0 && (
            <div style={SECTION}>
              <div style={SECTION_LABEL}>Car service</div>
              {carServices.map(c => {
                const company = c.carServiceCompany || c.carServiceDriver || c.driverName || 'Car';
                const pickupAttached = sheetEvents.some(e => e.travelId === c.id && e.travelType === 'carservice_pickup');
                const dropoffAttached = sheetEvents.some(e => e.travelId === c.id && e.travelType === 'carservice_dropoff');
                return (
                  <div key={c.id} style={{ marginBottom: 8 }}>
                    <div style={{ display:'flex', alignItems:'stretch', gap:6, marginBottom: 4 }}>
                      <div
                        style={{ ...PALETTE_BTN, marginBottom: 0, cursor: 'default', flex: 1 }}
                      >
                        <span style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600 }}>{company}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                            {c.carServiceTime ? formatTime(c.carServiceTime) : ''}{c.carServicePickup ? ` · ${c.carServicePickup}` : ''}
                          </div>
                        </span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteHeroChip(c); }}
                        title="Permanently delete this car service"
                        style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, padding:'0 12px', cursor:'pointer', color:'#dc2626', display:'flex', alignItems:'center', justifyContent:'center' }}
                      >
                        <i className="fa-solid fa-trash-can" style={{ fontSize: 12 }}></i>
                      </button>
                    </div>
                    <button
                      style={{ ...PALETTE_BTN, marginLeft: 16, padding: '8px 12px', opacity: pickupAttached ? 0.5 : 1 }}
                      disabled={pickupAttached}
                      onClick={() => addCarLeg(c, 'pickup')}
                    >
                      <span style={{ flex: 1, fontSize: 12 }}>
                        <span style={{ fontWeight: 600 }}>Pickup</span>
                        {c.carServiceTime && <span style={{ color: 'var(--text3)', marginLeft: 8 }}>{formatTime(c.carServiceTime)}</span>}
                        {c.carServicePickup && <span style={{ color: 'var(--text3)', marginLeft: 6 }}>· {c.carServicePickup}</span>}
                      </span>
                      <i className={`fa-solid ${pickupAttached ? 'fa-check' : 'fa-plus'}`} style={{ fontSize: 10, color: pickupAttached ? '#22c55e' : 'var(--text3)' }}></i>
                    </button>
                    {c.carServiceDropoff && (
                      <button
                        style={{ ...PALETTE_BTN, marginLeft: 16, padding: '8px 12px', opacity: dropoffAttached ? 0.5 : 1 }}
                        disabled={dropoffAttached}
                        onClick={() => addCarLeg(c, 'dropoff')}
                      >
                        <span style={{ flex: 1, fontSize: 12 }}>
                          <span style={{ fontWeight: 600 }}>Dropoff</span>
                          <span style={{ color: 'var(--text3)', marginLeft: 6 }}>· {c.carServiceDropoff}</span>
                        </span>
                        <i className={`fa-solid ${dropoffAttached ? 'fa-check' : 'fa-plus'}`} style={{ fontSize: 10, color: dropoffAttached ? '#22c55e' : 'var(--text3)' }}></i>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {venueCatalog.length > 0 && (
            <div style={SECTION}>
              <div style={SECTION_LABEL}>Venues</div>
              {venueCatalog.map(v => {
                const attached = sheetEvents.some(e => e.id === v.id);
                const slots = timingSlotsFor(v);
                return (
                  <div key={v.id} style={{ marginBottom: 8 }}>
                    <button
                      style={{ ...PALETTE_BTN, opacity: attached ? 0.5 : 1, marginBottom: slots.length ? 4 : 6 }}
                      disabled={attached}
                      onClick={() => attachVenueToSheet(v)}
                      title="Attach to current sheet"
                    >
                      <span style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{v.title || 'Venue'}</div>
                        {v.location && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{v.location}</div>}
                      </span>
                      <i className={`fa-solid ${attached ? 'fa-check' : 'fa-plus'}`} style={{ fontSize: 11, color: attached ? '#22c55e' : 'var(--text3)' }}></i>
                    </button>
                    {/* Timing slots parsed from the venue's notes
                        (soundcheck / doors / showtime). Each shows as
                        an individually-addable palette item; clicking
                        creates a real event on today's sheet. */}
                    {slots.map(s => (
                      <button
                        key={s.label}
                        style={{ ...PALETTE_BTN, marginLeft: 16, padding: '8px 12px', opacity: s.attached ? 0.5 : 1 }}
                        disabled={s.attached}
                        onClick={() => addVenueSlotToSheet(s, v)}
                      >
                        <span style={{ flex: 1, fontSize: 12 }}>
                          <span style={{ fontWeight: 600 }}>{s.label}</span>
                          <span style={{ color: 'var(--text3)', marginLeft: 8 }}>{formatTime(s.time)}</span>
                        </span>
                        <i className={`fa-solid ${s.attached ? 'fa-check' : 'fa-plus'}`} style={{ fontSize: 10, color: s.attached ? '#22c55e' : 'var(--text3)' }}></i>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* RIGHT — assembled timeline (mirrors published-sheet look) */}
        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          {/* Top accent strip — solid for one artist, multi-color
              gradient for joint sheets. Same SheetBuilder pattern. */}
          {(() => {
            const colors = selArtistIds.map(id => artists.find(a => a.id === id)?.color || '#1E40AF');
            const accent = colors[0] || '#1E40AF';
            const bg = colors.length > 1 ? `linear-gradient(to right, ${colors.join(', ')})` : accent;
            return <div style={{ height: 4, background: bg }} />;
          })()}
          <div style={{ padding: 20 }}>
          {/* Action bar — status pill + Publish/Unpublish + Send Now.
              Lives at the top of the right rail so the build → ship
              flow stays in one view. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 10px', borderRadius: 999,
              fontSize: 11, fontWeight: 800, letterSpacing: '0.06em',
              background: sheetStatus === 'published' ? 'rgba(34,197,94,0.12)' : 'rgba(148,163,184,0.15)',
              color: sheetStatus === 'published' ? '#16a34a' : 'var(--text2)',
              border: `1px solid ${sheetStatus === 'published' ? 'rgba(34,197,94,0.3)' : 'var(--border)'}`,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: sheetStatus === 'published' ? '#16a34a' : '#94a3b8' }} />
              {sheetStatus === 'published' ? 'PUBLISHED' : 'DRAFT'}
            </div>
            <div style={{ flex: 1 }} />
            <button
              onClick={togglePublish}
              disabled={!sheetId || publishing}
              style={{
                padding: '8px 14px', borderRadius: 8,
                background: sheetStatus === 'published' ? 'transparent' : '#1E40AF',
                color: sheetStatus === 'published' ? 'var(--text)' : '#fff',
                border: `1px solid ${sheetStatus === 'published' ? 'var(--border)' : '#1E40AF'}`,
                fontSize: 12, fontWeight: 700,
                cursor: sheetId && !publishing ? 'pointer' : 'not-allowed',
                opacity: !sheetId ? 0.5 : 1,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <i className={`fa-solid ${sheetStatus === 'published' ? 'fa-rotate-left' : 'fa-paper-plane'}`} style={{ fontSize: 11 }}></i>
              {publishing ? '…' : (sheetStatus === 'published' ? 'Unpublish' : 'Publish')}
            </button>
            <button
              onClick={openSendModal}
              disabled={!sheetId}
              style={{
                padding: '8px 14px', borderRadius: 8,
                background: '#06B6D4', color: '#fff',
                border: '1px solid #06B6D4',
                fontSize: 12, fontWeight: 700,
                cursor: sheetId ? 'pointer' : 'not-allowed',
                opacity: !sheetId ? 0.5 : 1,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <i className="fa-solid fa-envelope" style={{ fontSize: 11 }}></i>
              Send email
            </button>
          </div>
          {/* Header — date + artist, like the rendered sheet's masthead. */}
          <div style={{ textAlign: 'center', marginBottom: 18, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
            {/* Joint-sheet artist avatars — overlap stack like the
                published sheet so the gradient strip pairs with a
                visual key for who's on the sheet. */}
            {selArtistIds.length > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 10 }}>
                {selArtistIds.map(id => {
                  const a = artists.find(x => x.id === id);
                  if (!a) return null;
                  const color = a.color || '#1E40AF';
                  return (
                    <div key={id} style={{ width: 44, height: 44, borderRadius: '50%', overflow: 'hidden', border: `2.5px solid ${color}`, flexShrink: 0, background: `${color}20` }}>
                      {a.photo ? (
                        <img src={a.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: '#fff' }}>
                          {a.emoji || a.name?.charAt(0)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ fontSize: 22, fontWeight: 800 }}>
              {(() => {
                const names = selArtistIds.map(id => artists.find(a => a.id === id)?.name).filter(Boolean);
                if (names.length === 0) return 'Day Sheet';
                return `${names.join(' & ')} Day Sheet`;
              })()}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>{date ? new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : ''}</span>
              {sheetTimezone && (
                <span title={sheetTimezone} style={{
                  fontSize: 10, fontWeight: 800, letterSpacing: '0.05em',
                  padding: '2px 7px', borderRadius: 999,
                  background: 'var(--bg2)', border: '1px solid var(--border)',
                  color: 'var(--text2)',
                }}>
                  {shortTimezone(sheetTimezone)}
                </span>
              )}
            </div>
            {/* Hero strip — weather + flight / hotel / car / venue
                chips, like the iOS QuickAccessStrip on Today. Weather
                rides as the leftmost card so the row reads
                weather → travel → venue at a glance, all unified to
                one tile style. */}
            {(() => {
              const venueEvents = sheetEvents
                .filter(e => e.travelType === 'venue')
                .map(e => ({ id: `event-${e.id}`, _eventId: e.id, type: 'venue', name: e.title, location: e.location }));
              const weatherChip = (city && weather?.tempHigh != null) ? [{
                id: 'weather',
                type: 'weather',
                city,
                tempHigh: weather.tempHigh,
                tempLow: weather.tempLow,
                condition: weather.condition,
              }] : [];
              const all = [...weatherChip, ...heroChips, ...venueEvents];
              if (all.length === 0) return null;
              return (
                <div style={{
                  // Flex-wrap with center justification so partial
                  // rows balance:
                  //   1 chip  → centered
                  //   2 chips → split-center pair
                  //   3 chips → full row, 2nd in middle
                  //   4 chips → 3 on top, 4th centered alone below
                  //   5 chips → 3 on top, 2 centered as pair below
                  // Chips claim ~1/3 of the row each, so wrapping
                  // happens naturally past 3 per row.
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 8,
                  marginTop: 14,
                }}>
                  {all.map(t => (
                    <HeroChip
                      key={t.id}
                      record={t}
                      artists={artists}
                      onClick={t.type === 'weather' ? undefined : () => openHeroChip(t)}
                      onDelete={t.type === 'weather' ? undefined : () => deleteHeroChip(t)}
                    />
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Yellow note card. Drop target — drag a timeline event in to
              append "{title} at {time} — {location}" to the body. */}
          {showNoteCard && (
            <NoteCard
              value={sheetNotes}
              onChange={setSheetNotes}
              onClose={() => { setSheetNotes(''); setShowNoteCard(false); }}
            />
          )}

          {sortedSheetEvents.length === 0 && (
            <div style={{ color: 'var(--text3)', fontSize: 13, padding: '20px 4px', textAlign: 'center' }}>
              Empty — click a palette item on the left to add it here.
            </div>
          )}
          {(() => {
            // Render the timeline as zone-band sections. Today + nextDay
            // halves are grouped separately so the NEXT DAY divider sits
            // between them. For band sections, each row's time renders
            // in the band's local zone with no badge — the band header
            // already names the zone. Inline sections keep the existing
            // sheet-zone + badge behavior for outliers.
            const renderBandHeader = (label, short, key) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 8px' }}>
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                <div style={{
                  fontSize: 11, fontWeight: 800, letterSpacing: '0.10em', textTransform: 'uppercase',
                  padding: '4px 10px', borderRadius: 999,
                  background: 'rgba(30,64,175,0.10)', color: 'var(--text2)',
                  whiteSpace: 'nowrap',
                }}>
                  <span style={{ color: 'var(--text3)' }}>{label}{' · '}</span>
                  <span style={{ color: 'var(--blue)', fontWeight: 800 }}>{short || ''}</span>
                </div>
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              </div>
            );
            const renderRow = (ev, displayZone, suppressBadge) => {
              const persons = (ev.person || '').split(',').map(p => p.trim()).filter(Boolean);
              const primaryFirstName = (artists.find(a => a.id === artistId)?.name?.split(' ')[0] || '').toLowerCase();
              const isImpliedSolo = persons.length === 1
                && persons[0].toLowerCase() === primaryFirstName;
              const personColors = isImpliedSolo
                ? []
                : persons.map(p => findPersonColor(colorMap, p)).filter(c => c !== '#e6edf3');
              const hasPerson = personColors.length > 0;
              const stripeBg = personColors.length > 1
                ? `linear-gradient(to bottom, ${personColors.join(', ')})`
                : (hasPerson ? personColors[0] : 'var(--border2)');
              const titleGradient = personColors.length > 1
                ? `linear-gradient(to right, ${personColors.join(', ')})`
                : null;
              // When the row sits in a band, render time in the band's
              // zone via the canonical instant; suppress the badge since
              // the band header already names the zone. Otherwise fall
              // back to the legacy localizeEventTime path.
              let localTime = null;
              let sourceLabel = null;
              if (suppressBadge && ev.startInstant && displayZone) {
                localTime = formatInstantInZone(ev.startInstant, displayZone);
              } else {
                const r = localizeEventTime(ev, sheetTimezone, formatTime);
                localTime = r.localTime;
                sourceLabel = r.sourceLabel;
              }
              return (
                <React.Fragment key={ev.id}>
                <div
                  style={{
                    background: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: 12,
                    marginBottom: 8,
                    display: 'flex',
                    alignItems: 'stretch',
                    overflow: 'hidden',
                    cursor: 'pointer',
                  }}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('application/x-daysheets-event', JSON.stringify({
                      id: ev.id,
                      title: ev.title || '',
                      timeStart: ev.timeStart || null,
                      location: ev.location || null,
                    }));
                    e.dataTransfer.effectAllowed = 'copy';
                  }}
                  onClick={() => setEditingEvent(ev)}
                  title="Click to edit"
                >
                  {/* Who color stripe — left edge, solid for one
                      person, vertical gradient when multiple are
                      tagged. */}
                  <div style={{ width: 4, background: stripeBg, flexShrink: 0 }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', flex: 1 }}>
                    <div style={{ minWidth: 76, fontSize: 12, color: 'var(--text3)', fontWeight: 700, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                      <span>{localTime ? formatTime(localTime) : '—'}</span>
                      {sourceLabel && (
                        <span title={`Sheet shows ${shortTimezone(sheetTimezone)} time. Event was set in ${ev.timezone}.`} style={{
                          fontSize: 9, fontWeight: 800, letterSpacing: '0.04em',
                          padding: '1px 6px', borderRadius: 999,
                          background: 'rgba(245,158,11,0.15)',
                          color: '#b45309',
                          border: '1px solid rgba(245,158,11,0.4)',
                          whiteSpace: 'nowrap',
                        }}>
                          {sourceLabel}
                        </span>
                      )}
                    </div>
                    {ev.travelType && (() => {
                      // Compact travel-type icon next to the title so a
                      // user toggling "Travel Event → Flight" gets
                      // immediate visual confirmation. flight_departure /
                      // flight_arrival both render as plane.
                      const TYPE_ICON = {
                        flight: 'fa-plane', flight_departure: 'fa-plane-departure', flight_arrival: 'fa-plane-arrival',
                        hotel: 'fa-hotel', carservice: 'fa-car-side', car: 'fa-car',
                        ground: 'fa-van-shuttle', train: 'fa-train', venue: 'fa-landmark-dome',
                      };
                      const TYPE_COLOR = {
                        flight: '#1E40AF', flight_departure: '#1E40AF', flight_arrival: '#22c55e',
                        hotel: '#06B6D4', carservice: '#06B6D4', car: '#fbbf24',
                        ground: '#f472b6', train: '#a78bfa', venue: '#dc2626',
                      };
                      const ic = TYPE_ICON[ev.travelType];
                      const co = TYPE_COLOR[ev.travelType] || 'var(--blue)';
                      if (!ic) return null;
                      return (
                        <i className={`fa-solid ${ic}`} style={{ fontSize: 13, color: co, flexShrink: 0 }} title={ev.travelType.replace('_', ' ')}></i>
                      );
                    })()}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 700,
                        ...(titleGradient
                          ? { background: titleGradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }
                          : { color: hasPerson ? personColors[0] : 'var(--text)' }),
                      }}>{ev.title}</div>
                      {ev.location && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{ev.location}</div>}
                      {persons.length > 0 && !isImpliedSolo && (
                        <div style={{
                          fontSize: 11, fontWeight: 600, marginTop: 2,
                          ...(titleGradient
                            ? { background: titleGradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }
                            : { color: hasPerson ? personColors[0] : 'var(--text3)', opacity: hasPerson ? 0.85 : 1 }),
                          display: 'flex', alignItems: 'center', gap: 5,
                        }}>
                          <i className="fa-solid fa-user" style={{ fontSize: 9, color: hasPerson ? personColors[0] : 'var(--text3)' }}></i>
                          {ev.person}
                        </div>
                      )}
                    </div>
                    <button
                      style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px', fontSize: 11, cursor: 'pointer', color: 'var(--text3)' }}
                      onClick={(e) => { e.stopPropagation(); toggleNextDay(ev.id, !!ev.nextDay); }}
                      title="Toggle next-day"
                    >{ev.nextDay ? 'Today' : 'Next day'}</button>
                    <button
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 4 }}
                      onClick={(e) => { e.stopPropagation(); removeEventFromSheet(ev.id); }}
                      title="Remove from sheet (event stays in library)"
                    ><i className="fa-solid fa-xmark" style={{ fontSize: 13 }}></i></button>
                    <button
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 4 }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Permanently delete "${ev.title || 'event'}"?\n\nThis removes it from the database — it can't be re-added without recreating.`)) return;
                        try {
                          await api.deleteEvent(ev.id);
                          setSheetEvents(prev => prev.filter(x => x.id !== ev.id));
                          setAllArtistEvents(prev => prev.filter(x => x.id !== ev.id));
                        } catch (err) {
                          alert('Could not delete: ' + (err.message || 'unknown'));
                        }
                      }}
                      title="Permanently delete this event"
                    ><i className="fa-solid fa-trash-can" style={{ fontSize: 13, color: '#dc2626' }}></i></button>
                  </div>
                </div>
              </React.Fragment>
              );
            };
            const renderHalf = (sections, prefix) =>
              sections.flatMap((sec, si) => {
                const head = sec.kind === 'band'
                  ? [renderBandHeader(sec.label, sec.short, `${prefix}-band-${si}`)]
                  : [];
                const rows = sec.events.map(ev =>
                  renderRow(
                    ev,
                    sec.kind === 'band' ? sec.zone : sheetTimezone,
                    sec.kind === 'band'
                  )
                );
                return [...head, ...rows];
              });
            const todayRows = renderHalf(timelineSections.today, 'td');
            const nextDayRows = renderHalf(timelineSections.nextDay, 'nd');
            const divider = timelineSections.hasNextDay
              ? [
                  <div key="next-day-divider" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '14px 0 10px', color: 'var(--text3)', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em' }}>
                    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                    NEXT DAY
                    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                  </div>,
                ]
              : [];
            return [...todayRows, ...divider, ...nextDayRows];
          })()}
          </div>
        </div>
      </div>

      {travelModalType && (
        <TravelFormModal
          record={{ type: travelModalType }}
          artistId={artistId}
          artists={artists}
          defaultDate={date}
          lockedType={travelModalType}
          onSave={handleTravelModalSave}
          onClose={() => setTravelModalType(null)}
        />
      )}
      {editingTravel && (
        <TravelFormModal
          record={editingTravel}
          artistId={artistId}
          artists={artists}
          defaultDate={date}
          lockedType={editingTravel.type}
          onSave={async () => {
            setEditingTravel(null);
            // Refresh travel + sheet events so renamed/retimed
            // chips re-render with the latest values.
            const fresh = await api.getAllTravel(artistId);
            setTravel(Array.isArray(fresh) ? fresh : (fresh?.travel || []));
            if (sheetId) {
              const ev = await api.getEvents(sheetId);
              setSheetEvents(prev => mergeEvents(prev, ev.events || []));
            }
          }}
          onClose={() => setEditingTravel(null)}
        />
      )}
      {newEventOpen && (
        <EventModal
          event={eventSeed}
          onSave={createEvent}
          onClose={() => { setNewEventOpen(false); setEventSeed(null); }}
          sheetArtistId={artistId}
          sheetDate={date}
        />
      )}
      {editingEvent && (
        <EventModal
          event={editingEvent}
          onSave={(payload) => updateEvent(editingEvent.id, payload)}
          onClose={() => setEditingEvent(null)}
          sheetArtistId={artistId}
          sheetDate={date}
        />
      )}
      {sendOpen && sheetId && (() => {
        // Build the sheet shape SendModal expects — sheet object plus
        // an `artist` shortcut carrying the distribution lists. We
        // pull the artist record off the in-memory list so we don't
        // need a fresh fetch.
        const artistRecord = artists.find(a => a.id === artistId) || {};
        const sheetForSend = {
          id: sheetId,
          artistId,
          artistIds: selArtistIds.length > 1 ? selArtistIds : undefined,
          date,
          city,
          notes: sheetNotes,
          status: sheetStatus,
          artist: {
            distributionContacts: artistRecord.distributionContacts || [],
            distributionGroups: artistRecord.distributionGroups || [],
            distributionList: artistRecord.distributionList || [],
          },
        };
        return (
          <SendModal
            sheet={sheetForSend}
            onClose={() => setSendOpen(false)}
            onSent={() => setSendOpen(false)}
          />
        );
      })()}
      {venueModalOpen && (
        <VenueModal
          defaultDate={date}
          onSave={createVenue}
          onClose={() => setVenueModalOpen(false)}
        />
      )}
    </div>
  );
}


// Venue create modal. Smart name field hits Google Places autocomplete
// — picking a result auto-splits the prediction "Madison Square Garden,
// 4 Pennsylvania Plaza, NY" into name (first segment) and address (the
// rest), so the user types once and gets both. Soundcheck / doors /
// showtime / capacity / production contact round out the call-sheet
// essentials.
function VenueModal({ defaultDate, onSave, onClose }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [eventDate, setEventDate] = useState(defaultDate || '');
  const [soundcheck, setSoundcheck] = useState('');
  const [doors, setDoors] = useState('');
  const [showtime, setShowtime] = useState('');
  const [capacity, setCapacity] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  // Openers — typed array so multi-act bills (Olivia → Lauv → JoBros) can
  // each surface as their own bold "OPENER:" row in the distribution
  // email. Order in the array == order on the bill (top → main support).
  const [openers, setOpeners] = useState([]);
  // Captured from the autocomplete pick so the iOS venue detail card
  // can enrich with photos / website / phone via /places/details.
  // Cleared when the user manually retypes the name.
  const [placeId, setPlaceId] = useState(null);

  // Places autocomplete — only fires when the field is actively typed
  // into. Picking a prediction sets both the name and the address.
  const [predictions, setPredictions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const debounceRef = React.useRef(null);

  function searchPlaces(text) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!text || text.length < 2) { setPredictions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.placesAuto(text);
        setPredictions(res?.predictions || []);
      } catch { setPredictions([]); }
    }, 300);
  }

  function pickPrediction(prediction) {
    const desc = prediction.description || '';
    // Predictions look like "Venue Name, Street, City, ST, Country" —
    // split on the first comma so the name keeps any internal commas
    // off the address line.
    const idx = desc.indexOf(',');
    if (idx === -1) {
      setName(desc);
      setAddress('');
    } else {
      setName(desc.slice(0, idx).trim());
      setAddress(desc.slice(idx + 1).trim());
    }
    setPlaceId(prediction.placeId || null);
    setPredictions([]);
    setShowSuggest(false);
  }

  const fieldStyle = {
    width: '100%', boxSizing: 'border-box', background: 'var(--bg)',
    border: '1px solid var(--border)', borderRadius: 8, padding: '9px 12px',
    color: 'var(--text)', fontSize: 14, marginBottom: 10,
  };
  const labelStyle = { fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 4, display: 'block' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={onClose}>
      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 22, width: 480, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 14px', fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>
          <i className="fa-solid fa-landmark-dome" style={{ marginRight: 8, color: '#dc2626' }}></i>
          New venue
        </h3>

        <label style={labelStyle}>Venue name</label>
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <input
            value={name}
            onChange={e => {
              setName(e.target.value);
              searchPlaces(e.target.value);
              setShowSuggest(true);
              if (placeId) setPlaceId(null);
            }}
            onFocus={() => setShowSuggest(true)}
            onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
            placeholder="Type to search venues…"
            autoFocus
            style={{ ...fieldStyle, marginBottom: 0 }}
          />
          {showSuggest && predictions.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 200 }}>
              {predictions.map((p, i) => (
                <div
                  key={i}
                  onMouseDown={() => pickPrediction(p)}
                  style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: 'var(--text)', borderBottom: i < predictions.length - 1 ? '1px solid var(--border)' : 'none' }}
                >📍 {p.description}</div>
              ))}
            </div>
          )}
        </div>

        <label style={labelStyle}>Address</label>
        <input value={address} onChange={e => setAddress(e.target.value)} placeholder="Auto-fills from venue search" style={fieldStyle} />

        <label style={labelStyle}>Date</label>
        <input type="date" value={eventDate} onChange={e => setEventDate(e.target.value)} style={fieldStyle} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <div>
            <label style={labelStyle}>Soundcheck</label>
            <input type="time" value={soundcheck} onChange={e => setSoundcheck(e.target.value)} style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>Doors</label>
            <input type="time" value={doors} onChange={e => setDoors(e.target.value)} style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>Showtime</label>
            <input type="time" value={showtime} onChange={e => setShowtime(e.target.value)} style={fieldStyle} />
          </div>
        </div>

        <label style={labelStyle}>Openers</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
          {openers.map((op, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                value={op.name}
                onChange={e => setOpeners(prev => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                placeholder={`Opener ${i + 1} name`}
                style={{ ...fieldStyle, marginBottom: 0, flex: 2 }}
              />
              <input
                type="time"
                value={op.time || ''}
                onChange={e => setOpeners(prev => prev.map((x, j) => j === i ? { ...x, time: e.target.value } : x))}
                style={{ ...fieldStyle, marginBottom: 0, flex: 1 }}
              />
              <button
                type="button"
                onClick={() => setOpeners(prev => prev.filter((_, j) => j !== i))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 14, padding: '0 6px' }}
                aria-label="Remove opener"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setOpeners(prev => [...prev, { name: '', time: '' }])}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 12px', background: 'var(--bg)', border: '1px dashed var(--border)', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: 'var(--text3)', fontFamily: 'inherit' }}
          >
            <i className="fa-solid fa-plus" style={{ fontSize: 10 }}></i> Add opener
          </button>
        </div>

        <label style={labelStyle}>Capacity</label>
        <input value={capacity} onChange={e => setCapacity(e.target.value)} placeholder="e.g. 1500" style={fieldStyle} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={labelStyle}>Production contact</label>
            <input value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Name" style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>Phone</label>
            <input value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="555-555-5555" style={fieldStyle} />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
          <button
            onClick={onClose}
            style={{ padding: '9px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', color: 'var(--text)' }}
          >Cancel</button>
          <button
            onClick={() => name && onSave({ name, address, soundcheck, doors, showtime, capacity, contactName, contactPhone, placeId, openers: openers.filter(o => (o.name || '').trim()) })}
            disabled={!name}
            style={{
              padding: '9px 16px',
              background: name ? '#1E40AF' : '#94a3b8',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: name ? 'pointer' : 'not-allowed',
              fontWeight: 700,
              minWidth: 110,
            }}
          >Save venue</button>
        </div>
      </div>
    </div>
  );
}

// Hero strip chip — compact card mirroring the SheetBuilder tCard
// style on the published sheet's icon row. Same rules everywhere:
// 1.5px ${color}30 border, ${color}06 fill, fa-solid icon at 18px,
// 9-10px label.
function HeroChip({ record, onClick, onDelete, artists }) {
  const [hover, setHover] = useState(false);
  // Weather icon mirrors the iOS condition→symbol map (cloud.fill →
  // fa-cloud, sun.max.fill → fa-sun, etc.) so admin and viewer agree
  // on which glyph means what.
  function weatherIcon(condition) {
    const c = (condition || '').toLowerCase();
    if (c.includes('rain'))  return 'fa-cloud-showers-heavy';
    if (c.includes('snow'))  return 'fa-snowflake';
    if (c.includes('storm') || c.includes('thunder')) return 'fa-cloud-bolt';
    if (c.includes('cloud') || c.includes('fog'))    return 'fa-cloud';
    if (c.includes('clear') || c.includes('sun'))    return 'fa-sun';
    return 'fa-cloud-sun';
  }
  const ICON = {
    flight: 'fa-plane', hotel: 'fa-hotel', carservice: 'fa-car-side',
    car: 'fa-car', ground: 'fa-van-shuttle', train: 'fa-train',
    venue: 'fa-landmark-dome',
    weather: weatherIcon(record.condition),
  };
  const ACCENT = {
    flight: '#1E40AF', hotel: '#06B6D4', carservice: '#06B6D4',
    car: '#fbbf24', ground: '#f472b6', train: '#a78bfa', venue: '#dc2626',
    weather: '#0EA5E9',
  };
  const accent = ACCENT[record.type] || '#1E40AF';
  let primary = '';
  let secondary = '';
  if (record.type === 'flight') {
    // Lead-passenger initials beneath the route — same iOS chip
    // style. Falls back to flight code if there's no passenger.
    primary = `${record.departureAirport || '?'} → ${record.arrivalAirport || '?'}`;
    const pax = (record.passenger || '').split(',').map(p => p.trim()).filter(Boolean);
    const lead = record.leadPassenger || pax[0] || '';
    const initials = lead ? resolveInitials(lead, artists || []) : '';
    secondary = initials || [record.airline, record.flightNumber].filter(Boolean).join(' ');
  } else if (record.type === 'hotel') {
    primary = record.hotelName || 'Hotel';
    secondary = record.hotelAddress?.split(',').slice(0, 2).join(',') || '';
  } else if (record.type === 'carservice') {
    primary = record.carServiceCompany || 'Car';
    // Format scheduled pickup time as 12-hour so the chip reads
    // "8:30 PM" instead of the raw "20:30".
    secondary = record.carServiceTime ? formatTime(record.carServiceTime) : '';
  } else if (record.type === 'venue') {
    primary = record.name || 'Venue';
    secondary = record.location?.split(',').slice(0, 2).join(',') || '';
  } else if (record.type === 'weather') {
    // Big high temp on top, city beneath — matches iOS weatherTile.
    primary = record.tempHigh != null
      ? `${Math.round(record.tempHigh)}° / ${Math.round(record.tempLow)}°`
      : '—';
    secondary = (record.city || '').split(',')[0].trim();
  } else {
    primary = record.driverName || record.trainCompany || record.carCompany || record.type;
  }
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      title="Click to edit"
      style={{
        // Each chip claims ~1/3 of the row so 3 fit per row and a
        // 4th wraps cleanly. Min width keeps narrow rails readable.
        flex: '0 1 calc(33.333% - 6px)',
        minWidth: 110, maxWidth: 200,
        padding: 12, borderRadius: 12,
        border: `1.5px solid ${accent}30`, background: `${accent}06`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
        position: 'relative', cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s',
        transform: hover ? 'translateY(-1px)' : 'none',
        boxShadow: hover ? `0 4px 12px ${accent}25` : 'none',
      }}
    >
      {onDelete && (
        <button
          onClick={e => { e.stopPropagation(); onDelete(); }}
          title="Delete"
          style={{
            position: 'absolute', top: -8, right: -8,
            width: 20, height: 20, borderRadius: '50%',
            background: '#ef4444', border: '2px solid var(--bg)',
            color: '#fff', cursor: 'pointer', padding: 0,
            display: hover ? 'flex' : 'none',
            alignItems: 'center', justifyContent: 'center',
            zIndex: 1,
          }}
        >
          <i className="fa-solid fa-xmark" style={{ fontSize: 9 }}></i>
        </button>
      )}
      <i className={`fa-solid ${ICON[record.type] || 'fa-suitcase'}`} style={{ fontSize: 18, color: accent }}></i>
      <span style={{ fontSize: 9, fontWeight: 800, color: accent, textAlign: 'center' }}>{primary}</span>
      {secondary && <span style={{ fontSize: 9, fontWeight: 700, color: accent, opacity: 0.7, textAlign: 'center' }}>{secondary}</span>}
    </div>
  );
}

// Yellow note card — lives at the top of the right-side timeline,
// styled like the NOTE block on a published day sheet (amber bg, pin
// icon, left accent stripe). The textarea is a drop target: dropping a
// timeline event row appends a "{title} at {time} — {location}" line
// to the note body without retyping. The parent owns sheetNotes and
// the debounced PATCH so this component stays pure.
function NoteCard({ value, onChange, onClose }) {
  const [isOver, setIsOver] = useState(false);

  function formatRef(ev) {
    const time = ev.timeStart ? (() => {
      const [h, m] = ev.timeStart.split(':').map(Number);
      const ampm = h >= 12 ? 'PM' : 'AM';
      return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
    })() : null;
    const parts = [];
    if (ev.title) parts.push(ev.title);
    if (time) parts.push(`at ${time}`);
    if (ev.location) parts.push(`— ${ev.location}`);
    return parts.join(' ');
  }

  return (
    <div
      style={{
        background: '#fff7d6',
        borderLeft: '4px solid #f59e0b',
        borderRadius: 12,
        padding: 14,
        marginBottom: 16,
        outline: isOver ? '2px dashed #f59e0b' : 'none',
        outlineOffset: -4,
        transition: 'outline 0.1s',
      }}
      onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setIsOver(true); }}
      onDragLeave={() => setIsOver(false)}
      onDrop={e => {
        e.preventDefault();
        setIsOver(false);
        const raw = e.dataTransfer.getData('application/x-daysheets-event');
        if (!raw) return;
        try {
          const ev = JSON.parse(raw);
          const line = formatRef(ev);
          if (!line) return;
          onChange(value ? `${value}\n${line}` : line);
        } catch {}
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#b45309', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em' }}>
          <i className="fa-solid fa-thumbtack" style={{ fontSize: 11 }}></i> NOTE
        </div>
        <button onClick={onClose} title="Remove note"
          style={{ background: 'transparent', border: 'none', color: '#b45309', cursor: 'pointer', padding: 4 }}>
          <i className="fa-solid fa-xmark" style={{ fontSize: 13 }}></i>
        </button>
      </div>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Type a note, or drag a timeline event in here…"
        style={{
          width: '100%', boxSizing: 'border-box',
          background: 'transparent', border: 'none',
          color: '#78350f', fontFamily: 'inherit', fontSize: 14,
          resize: 'vertical', minHeight: 50, outline: 'none',
        }}
      />
    </div>
  );
}

